/**
 * Main Assistant Runtime Context
 * Provides the core context and provider implementation
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useAuth } from '@/context/AuthContext';
import { useProjectContext } from '../ProjectContext';
import { useAgentConfig } from '../AgentConfigContext';
import {
  LLMEventStreamProcessor,
  type MessageStateManager,
} from '@/services/LLMEventStreamProcessor';
import type { ThreadMessage } from '@/types/messages';
import type { AskUserReply } from '@/types/tools';
import { useStreamSubscription } from './communication/StreamSubscriptionManager';
import { useMessageSender } from './communication/useMessageSender';
import { useMessageOperations } from './messages/useMessageOperations';
import { useAssistantState } from './state/useAssistantState';
import { useThreadManager } from './threads/useThreadManager';
import { useStreamingPoller } from './threads/useStreamingPoller';
import { useAnswerCardPoller } from './threads/useAnswerCardPoller';
import { usePlanMode } from '@/context/PlanModeContext';
import { useTodosOptional } from '@/context/TodoContext';
import { logger } from '@/utils/logger';

export interface AssistantRuntimeContext {
  // Message state
  messages: ThreadMessage[];
  isLoading: boolean;
  error: string | null;

  // Thread management
  currentThreadId: string | null;
  /**
   * Title of the thread on screen, when one is known.
   *
   * Threads are loaded by the sidebar, not by this context, so the title has no
   * other route down to a message component. The PDF export needs it because a
   * document headed by its thread's name is far more use later than one headed
   * by a message id.
   */
  currentThreadTitle: string | null;
  setCurrentThreadTitle: (title: string | null) => void;
  switchToThread: (
    projectId: string,
    threadId: string,
    recoveryMessageId?: string,
  ) => Promise<void>;
  createNewThread: () => void;

  sendMessage: (
    content: string,
    extraMetadata?: Record<string, unknown>
  ) => Promise<void>;
  cancelMessage: () => void;
  /** Answers or skips the ask_user call waiting in a running turn. */
  replyToAskUser: (
    threadId: string,
    toolCallId: string,
    reply: AskUserReply,
  ) => Promise<void>;

  // Stream data
  availableIds: Set<string>;
  addAvailableIds: (ids: string[]) => void;

  // Parallel streaming
  streamingThreadIds: Set<string>;

  // Message utilities
  getMessagesByThread: (threadId: string) => ThreadMessage[];
  clearThread: (threadId: string) => void;

  // Callbacks
  onThreadCreated?: (
    thread?: { id: string; projectId: string; title?: string }
  ) => void;
  onMessageUpdate?: (messages: ThreadMessage[]) => void;
}

const AssistantRuntimeContext = createContext<AssistantRuntimeContext | null>(
  null
);

export function AssistantRuntimeProvider({
  children,
  onThreadCreated,
  onMessageUpdate,
  initialThreadId,
  initialRecoveryMessageId,
}: {
  children: React.ReactNode;
  onThreadCreated?: (
    thread?: { id: string; projectId: string; title?: string }
  ) => void;
  onMessageUpdate?: (messages: ThreadMessage[]) => void;
  initialThreadId?: string;
  initialRecoveryMessageId?: string | null;
}) {
  // External dependencies
  const { accessToken } = useAuth();
  const selectedProject = useProjectContext();
  const { agentConfig, resetPlanMode: resetAgentPlanMode } = useAgentConfig();
  const { extractAndSetPlan, clearPlan } = usePlanMode();
  const todosContext = useTodosOptional();

  // State management
  const {
    messages,
    isLoading,
    error,
    currentThreadId,
    availableIds,
    streamingThreadIds,
    setMessages,
    setError,
    setCurrentThreadId,
    setAvailableIds,
    addAvailableIds,
    setThreadStreaming,
  } = useAssistantState();

  // Owned here rather than in `useAssistantState` because it is display-only:
  // the sidebar pushes it when a thread is opened or renamed, and nothing in
  // the message pipeline reads it.
  const [currentThreadTitle, setCurrentThreadTitle] = useState<string | null>(
    null,
  );

  // Create stable refs for current values
  const currentThreadIdRef = useRef(currentThreadId);
  const messagesRef = useRef(messages);
  const setMessagesRef = useRef(setMessages);

  // Update refs on every render
  currentThreadIdRef.current = currentThreadId;
  messagesRef.current = messages;
  setMessagesRef.current = setMessages;

  // Create stable MessageStateManager implementation
  const messageStateManager = useRef<MessageStateManager | undefined>(
    undefined
  );
  if (!messageStateManager.current) {
    // Upsert a message into an array: replace if found, append otherwise.
    const upsertMessage = (
      list: ThreadMessage[],
      message: ThreadMessage
    ): ThreadMessage[] => {
      const idx = list.findIndex((m) => m.id === message.id);
      if (idx >= 0) {
        const updated = [...list];
        updated[idx] = message;
        return updated;
      }
      return [...list, message];
    };

    messageStateManager.current = {
      getMessage: (messageId: string) => {
        return messagesRef.current.find((msg) => msg.id === messageId) || null;
      },
      addMessage: (message: ThreadMessage) => {
        messagesRef.current = upsertMessage(messagesRef.current, message);
        setMessages((prev) => upsertMessage(prev, message));
      },
      updateMessage: (
        messageId: string,
        updater: (message: ThreadMessage) => ThreadMessage
      ) => {
        const existing = messagesRef.current.find(
          (msg) => msg.id === messageId
        );
        if (!existing) {
          return null;
        }

        // Call updater once to get a stable result (avoids producing
        // different Date.now() / updatedAt values for ref vs React state).
        const updated = updater(existing);
        messagesRef.current = messagesRef.current.map((msg) =>
          msg.id === messageId ? updated : msg
        );
        setMessages((prev) =>
          prev.map((msg) => (msg.id === messageId ? updated : msg))
        );

        return updated;
      },
      getThreadMessages: (threadId: string) => {
        // No currentThreadId guard -- background streams must be
        // able to find their own thread's messages for tool-result
        // matching, FINISHED handling, etc.
        return messagesRef.current
          .filter((msg) => msg.threadId === threadId)
          .sort((a, b) => {
            const seqA = a.metadata?.sequenceNumber;
            const seqB = b.metadata?.sequenceNumber;

            if (seqA !== undefined && seqB !== undefined) {
              return seqA - seqB;
            }

            return a.createdAt.getTime() - b.createdAt.getTime();
          });
      },
    };
  }

  // Core services
  const streamProcessor = useRef<LLMEventStreamProcessor | undefined>(
    undefined
  );
  if (!streamProcessor.current) {
    streamProcessor.current = new LLMEventStreamProcessor(
      messageStateManager.current!
    );
  }

  // Message operations
  const messageOperations = useMessageOperations({
    messages,
    setMessages,
    currentThreadId,
  });

  // Thread management
  const { switchToThread, createNewThread } = useThreadManager({
    setCurrentThreadId,
    setMessages,
    setError,
    setAvailableIds,
    streamingThreadIds,
  });

  // Restore a thread when one is explicitly provided via the URL param.
  // Navigating to /reVeal without a thread ID always starts a new thread.
  //
  // The restore must be driven by *changes to the URL thread id*, not by
  // `currentThreadId` becoming null. react-router applies location updates
  // inside `startTransition`, so "New Thread" (which clears `currentThreadId`
  // as an urgent update, then navigates to /reVeal) renders once with the
  // thread already cleared while `initialThreadId` still holds the id the user
  // just left. Restoring there would reinstate that thread and the URL-sync
  // effect in ReasonerChat would push /reVeal/thread/<id> straight back, so the
  // current thread could never be cleared.
  const consumedUrlThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const projectId = selectedProject.selectedProjectId;
    if (!accessToken || !projectId) return;

    if (!initialThreadId) {
      // The URL no longer points at a thread. Forget what we consumed so a
      // later deep link back to the same id can still restore it.
      consumedUrlThreadIdRef.current = null;
      return;
    }

    if (consumedUrlThreadIdRef.current === initialThreadId) return;
    consumedUrlThreadIdRef.current = initialThreadId;

    // The runtime already owns a thread (sidebar selection, or a thread just
    // created by sendMessage) — the URL follows the runtime in those flows.
    if (currentThreadId) return;

    switchToThread(
      projectId,
      initialThreadId,
      initialRecoveryMessageId ?? undefined,
    ).catch((err) => {
      // Release the id so a later dependency change (e.g. a refreshed
      // accessToken) can retry. Marking it consumed above is what stops
      // duplicate attempts while the restore is in flight; keeping it
      // consumed after a failure would strand the deep link until the URL
      // drops the thread id or the provider remounts.
      consumedUrlThreadIdRef.current = null;
      logger.warn(
        '[AssistantRuntime] Failed to restore thread from URL:',
        err
      );
    });
  }, [
    accessToken,
    selectedProject.selectedProjectId,
    currentThreadId,
    initialThreadId,
    initialRecoveryMessageId,
    switchToThread,
  ]);

  // Message sending (per-thread abort + per-thread loading)
  const { sendMessage, cancelMessage, replyToAskUser } = useMessageSender({
    currentThreadId,
    selectedProjectId: selectedProject.selectedProjectId,
    agentConfig,
    accessToken: accessToken || '',
    streamProcessor: streamProcessor.current,
    streamingThreadIds,
    setThreadStreaming,
    setError,
    setCurrentThreadId,
    setMessages,
    onThreadCreated,
    currentThreadIdRef,
  });

  // Wrapped onMessageUpdate to include plan and todo detection
  const wrappedOnMessageUpdate = useCallback(
    (updatedMessages: ThreadMessage[]) => {
      if (currentThreadId && streamProcessor.current) {
        const allMessages =
          streamProcessor.current.getCurrentMessages(currentThreadId);
        extractAndSetPlan(allMessages);

        if (todosContext) {
          todosContext.extractTodosFromMessages(allMessages);
        }
      } else {
        logger.warn(
          `[AssistantRuntime] wrappedOnMessageUpdate: ` +
            `No threadId (${currentThreadId}) or streamProcessor`
        );
      }
      if (onMessageUpdate) {
        onMessageUpdate(updatedMessages);
      }
    },
    [currentThreadId, extractAndSetPlan, todosContext, onMessageUpdate]
  );

  // Stream subscription
  useStreamSubscription({
    streamProcessor: streamProcessor.current,
    currentThreadId,
    setAvailableIds,
    onMessageUpdate: wrappedOnMessageUpdate,
  });

  // Poll / reconnect for streaming messages (refresh & thread-switch recovery)
  useStreamingPoller({
    projectId: selectedProject.selectedProjectId,
    threadId: currentThreadId,
    messages,
    isLoading,
    setMessages,
    accessToken: accessToken || '',
    streamProcessor: streamProcessor.current,
  });

  useAnswerCardPoller({
    projectId: selectedProject.selectedProjectId,
    threadId: currentThreadId,
    messages,
    setMessages,
  });

  // Leaves the stream processor alone: the departing thread may still be
  // streaming, and every send or resume resets its own thread's state.
  const wrappedCreateNewThread = useCallback(() => {
    todosContext?.clearTodos();
    clearPlan();
    resetAgentPlanMode();
    createNewThread();
  }, [todosContext, clearPlan, resetAgentPlanMode, createNewThread]);

  const contextValue = useMemo<AssistantRuntimeContext>(
    () => ({
      messages,
      isLoading,
      error,
      currentThreadId,
      switchToThread,
      createNewThread: wrappedCreateNewThread,
      sendMessage,
      cancelMessage,
      replyToAskUser,
      availableIds,
      addAvailableIds,
      streamingThreadIds,
      getMessagesByThread: messageOperations.getMessagesByThread,
      clearThread: (threadId: string) => {
        messageOperations.clearThread(threadId);
        if (threadId === currentThreadId) {
          setAvailableIds(new Set());
        }
      },
      onThreadCreated,
      onMessageUpdate,
      currentThreadTitle,
      setCurrentThreadTitle,
    }),
    [
      messages,
      isLoading,
      error,
      currentThreadId,
      switchToThread,
      wrappedCreateNewThread,
      sendMessage,
      cancelMessage,
      replyToAskUser,
      availableIds,
      addAvailableIds,
      streamingThreadIds,
      messageOperations.getMessagesByThread,
      messageOperations.clearThread,
      setAvailableIds,
      onThreadCreated,
      onMessageUpdate,
      currentThreadTitle,
    ]
  );

  return (
    <AssistantRuntimeContext.Provider value={contextValue}>
      {children}
    </AssistantRuntimeContext.Provider>
  );
}

export function useAssistantRuntime() {
  const context = useContext(AssistantRuntimeContext);
  if (!context) {
    throw new Error(
      'useAssistantRuntime must be used within AssistantRuntimeProvider'
    );
  }
  return context;
}
